import { apiClient } from "./client";
import { MOCKS_ENABLED } from "./mock/registry";
import { mockDrugDb, mockMedicines } from "./mock/medicines";
import type { PageResponse } from "../types/patient";
import type {
  DrugDbItem,
  DrugRouteResponse,
  ImportFromDrugDbRequest,
  MedicineCategoryResponse,
  MedicineCreateRequest,
  MedicineListFilter,
  MedicineResponse,
  MedicineSummaryResponse,
  MedicineUpdateRequest,
} from "../types/medicine";

function buildParams(
  filter: MedicineListFilter | undefined,
  pageable: { page?: number; size?: number; sort?: string }
) {
  const p: Record<string, string | number | boolean> = {};
  if (filter?.q) p.q = filter.q;
  if (filter?.classification) p.classification = filter.classification;
  if (filter?.categoryId) p.categoryId = filter.categoryId;
  if (filter?.visibleOnly != null) p.visibleOnly = filter.visibleOnly;
  if (pageable.page != null) p.page = pageable.page;
  if (pageable.size != null) p.size = pageable.size;
  if (pageable.sort) p.sort = pageable.sort;
  return p;
}

const realMedicinesApi = {
  list(filter: MedicineListFilter | undefined, pageable: { page?: number; size?: number; sort?: string }) {
    return apiClient
      .get<PageResponse<MedicineSummaryResponse>>("/v1/medicines", {
        params: buildParams(filter, pageable),
      })
      .then((r) => r.data);
  },

  get(id: string) {
    return apiClient.get<MedicineResponse>(`/v1/medicines/${id}`).then((r) => r.data);
  },

  create(req: MedicineCreateRequest) {
    return apiClient.post<MedicineResponse>("/v1/medicines", req).then((r) => r.data);
  },

  update(id: string, req: MedicineUpdateRequest) {
    return apiClient.put<MedicineResponse>(`/v1/medicines/${id}`, req).then((r) => r.data);
  },

  show(id: string) {
    return apiClient.post<void>(`/v1/medicines/${id}/show`).then(() => undefined);
  },
  hide(id: string) {
    return apiClient.post<void>(`/v1/medicines/${id}/hide`).then(() => undefined);
  },
  deactivate(id: string) {
    return apiClient.post<void>(`/v1/medicines/${id}/disable`).then(() => undefined);
  },
  reactivate(id: string) {
    return apiClient.post<void>(`/v1/medicines/${id}/enable`).then(() => undefined);
  },
  softDelete(id: string) {
    return apiClient.delete<void>(`/v1/medicines/${id}`).then(() => undefined);
  },
  /** Hard delete (permanent). 409 nếu thuốc đang được tham chiếu. */
  remove(id: string) {
    return apiClient.delete<void>(`/v1/medicines/${id}/permanent`).then(() => undefined);
  },
  restore(id: string) {
    return apiClient.post<void>(`/v1/medicines/${id}/restore`).then(() => undefined);
  },

  importFromDrugDb(req: ImportFromDrugDbRequest) {
    return apiClient
      .post<MedicineResponse>("/v1/medicines/import-from-drug-db", req)
      .then((r) => r.data);
  },

  // Categories
  listCategories() {
    return apiClient
      .get<MedicineCategoryResponse[]>("/v1/medicine-categories")
      .then((r) => r.data);
  },
  createCategory(req: { code: string; name: string; sortOrder?: number }) {
    return apiClient
      .post<MedicineCategoryResponse>("/v1/medicine-categories", req)
      .then((r) => r.data);
  },

  // Drug routes
  listRoutes() {
    return apiClient.get<DrugRouteResponse[]>("/v1/drug-routes").then((r) => r.data);
  },
};

// Routes to the mock adapter until the real `/api/v1/medicines` is wired
// end-to-end (auth + tenant). Flip via `VITE_USE_MOCKS=false`.
export const medicinesApi = MOCKS_ENABLED ? mockMedicines : realMedicinesApi;

const realDrugDbApi = {
  search(q: string, limit = 20) {
    return apiClient
      .get<DrugDbItem[]>("/v1/drug-db/search", { params: { q, limit } })
      .then((r) => r.data);
  },
  byCode(nationalCode: string) {
    return apiClient.get<DrugDbItem>(`/v1/drug-db/${nationalCode}`).then((r) => r.data);
  },
  byBarcode(barcode: string) {
    return apiClient.get<DrugDbItem>(`/v1/drug-db/barcode/${barcode}`).then((r) => r.data);
  },
};

export const drugDbApi = MOCKS_ENABLED ? mockDrugDb : realDrugDbApi;

export const medicineQueryKeys = {
  all: ["medicines"] as const,
  list: (filter: MedicineListFilter | undefined, page: number) =>
    ["medicines", "list", filter ?? {}, page] as const,
  detail: (id: string) => ["medicines", "detail", id] as const,
  categories: () => ["medicine-categories"] as const,
  routes: () => ["drug-routes"] as const,
  drugDbSearch: (q: string) => ["drug-db", "search", q] as const,
};

/** One same-ingredient alternative, as the substitution picker shows it. */
export interface MedicineSubstitute {
  medicineId: string;
  name: string;
  activeIngredient: string | null;
  strength: string | null;
  dosageForm: string | null;
  routeOfAdministration: string | null;
  unit: string | null;
  sellPriceVnd: number | null;
  stockOnHand: number | null;
  inStock: boolean;
  /**
   * Differences from the medicine being replaced. False does NOT mean unusable — it means the dose
   * needs rethinking, which is the doctor's call. The backend deliberately never auto-substitutes.
   */
  sameStrength: boolean;
  sameDosageForm: boolean;
  sameRoute: boolean;
  isControlled: boolean;
}

export interface MedicineSubstitution {
  medicineId: string;
  stockOnHand: number | null;
  inStock: boolean;
  substitutes: MedicineSubstitute[];
}

export const substitutionApi = {
  /** Same-ingredient alternatives, best first (in stock, then closest to the original). */
  forMedicine(medicineId: string, branchId?: string | null, limit = 10) {
    return apiClient
      .get<MedicineSubstitution>(`/v1/medicines/${medicineId}/substitutes`, {
        params: { branchId: branchId || undefined, limit },
      })
      .then((r) => r.data);
  },
};

export const substitutionQueryKeys = {
  forMedicine: (medicineId: string, branchId?: string | null) =>
    ["medicines", "substitutes", medicineId, branchId ?? null] as const,
};
